import {
  Center,
  Group,
  LoadingOverlay,
  Stack,
  Tabs,
  Text,
  Title,
} from "@mantine/core"
import { InformationRequestStatus } from "@tfg/db"
import { Loader } from "@tfg/ui"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"

import { StatusBadge } from "~/components/Badge.tsx"
import { ActivitiesProvider } from "~/components/contexts/useActivitiesContext"
import { useFindCertificateRequestsForSupplier } from "~/gen/api"
import { useFindOperationChainsQuery } from "~/pages/Admin/Ingestion/OpChains/hooks/useFindOperationChainsQuery"
import { useListSupplierProductionSitesQuery } from "~/pages/SupplierPortal/ProductionSites/hooks/useListProductionSiteQuery.ts"
import { CertificateRequestsBatchModalProvider } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/certificateRequestsBatchModal/store/CertificateRequestsBatchModalContext"
import { TraceabilityRequestsList } from "~/pages/SupplierPortal/TraceabilityRequests/TraceabilityRequestsList.tsx"
import { CertificateRequests } from "~/pages/SupplierPortal/TraceabilityRequests/components/CertificateRequests.tsx"
import { ProdsiteRequests } from "~/pages/SupplierPortal/TraceabilityRequests/components/ProdsiteRequests.tsx"
import { useGetTraceabilityRequestsStats } from "~/pages/SupplierPortal/TraceabilityRequests/hooks/useGetTraceabilityRequestsStats"
import { useProductionSiteFieldRequestsQuery } from "~/pages/SupplierPortal/TraceabilityRequests/hooks.ts"
import {
  getCertificateRequestsFilteredLists,
  getOpenAndClosedProdsiteFieldRequests,
} from "~/pages/SupplierPortal/TraceabilityRequests/utils.ts"
import { useAuth } from "~/store/auth-context.tsx"

import { RequestTabHeader } from "./components/RequestTabHeader"

export const TraceabilityRequests = () => {
  const { t } = useTranslation(["traceability"])
  const { status } = useParams()
  const [statusDisplayed, setStatusDisplayed] =
    useState<InformationRequestStatus>(InformationRequestStatus.OPENED)
  const navigate = useNavigate()
  const { isAdmin, user } = useAuth()

  const {
    data: certificateRequests,
    isLoading: isCertificateRequestsLoading,
    isError: isCertificateRequestsError,
  } = useFindCertificateRequestsForSupplier()
  const {
    data: productionSiteFieldRequests,
    isLoading: isProductionSiteFieldRequestsLoading,
    isError: isProductionSiteFieldRequestsError,
  } = useProductionSiteFieldRequestsQuery()
  const { data: operationChainsResponse } = useFindOperationChainsQuery({})
  const { data: productionSites } = useListSupplierProductionSitesQuery() //TODO Should be passed down > TraceabilityRequestOperation > ProductionSiteSelect

  const canceled = () => {
    setStatusDisplayed(InformationRequestStatus.CANCELED)
    navigate("/supplier/requests/traceability")
  }

  useEffect(() => {
    if (
      !status ||
      (status !== "traceability" &&
        statusDisplayed === InformationRequestStatus.CANCELED) ||
      ![
        "prodsitefield",
        "traceability",
        "certificates_scope",
        "certificates_transaction",
      ].includes(status)
    ) {
      setStatusDisplayed(InformationRequestStatus.OPENED)
      navigate("/supplier/requests/traceability")
    }
  }, [navigate, status, statusDisplayed])

  const {
    openRequests: openProdsitefieldRequests,
    closedRequests: closedProdsitefieldRequests,
  } = getOpenAndClosedProdsiteFieldRequests(
    productionSiteFieldRequests,
    user?.id
  )

  const currentProdsitefieldRequests =
    statusDisplayed === InformationRequestStatus.OPENED
      ? openProdsitefieldRequests
      : closedProdsitefieldRequests

  const {
    openScopeCertificates,
    closedScopeCertificates,
    openTransactionCertificates,
    closedTransactionCertificates,
  } = getCertificateRequestsFilteredLists(certificateRequests?.data ?? [])

  const currentTransactionCertificates =
    statusDisplayed === InformationRequestStatus.OPENED
      ? openTransactionCertificates
      : closedTransactionCertificates

  const currentScopeCertificates =
    statusDisplayed === "OPENED"
      ? openScopeCertificates
      : closedScopeCertificates

  const {
    data: stats,
    isLoading: isStatsLoading,
    isError: isStatsError,
  } = useGetTraceabilityRequestsStats()
  if (isStatsError) {
    return (
      <Center h="100%">
        <Text c="red" size="xl">
          {t("request.errors.generic")}
        </Text>
      </Center>
    )
  }
  if (isStatsLoading) {
    return <Loader fullPage />
  }

  const totalOpenRequests =
    stats.opened +
    openProdsitefieldRequests.length +
    openScopeCertificates.length +
    openTransactionCertificates.length

  const totalClosedRequests =
    stats.closed +
    closedProdsitefieldRequests.length +
    closedScopeCertificates.length +
    closedTransactionCertificates.length

  return (
    <>
      <ActivitiesProvider>
        <LoadingOverlay visible={isStatsLoading} />
        <Tabs
          value={statusDisplayed}
          onChange={(v) =>
            v && setStatusDisplayed(v as InformationRequestStatus)
          }
        >
          <Group style={{ display: "flex", justifyContent: "space-between" }}>
            <Tabs.Tab value={InformationRequestStatus.OPENED}>
              <Group>
                <StatusBadge color="red" value={totalOpenRequests} />
                <Title order={4} fw={500}>
                  {t("requests.tabs.open")}
                </Title>
              </Group>
            </Tabs.Tab>
            <Group>
              <Tabs.Tab value={InformationRequestStatus.CLOSED}>
                <Group>
                  <StatusBadge color="green" value={totalClosedRequests} />
                  <Title order={4} fw={500}>
                    {t("requests.tabs.closed")}
                  </Title>
                </Group>
              </Tabs.Tab>
              {isAdmin && (
                <Tabs.Tab value={InformationRequestStatus.CANCELED}>
                  <Group
                    onClick={() => canceled()}
                    style={{ cursor: "pointer" }}
                  >
                    <Group style={{ cursor: "pointer" }}>
                      <StatusBadge color="violet" value={stats.canceled} />
                      <Title c="violet" order={4} fw={500}>
                        Canceled Request
                      </Title>
                    </Group>
                  </Group>
                </Tabs.Tab>
              )}
            </Group>
          </Group>
        </Tabs>
        <Tabs
          value={status}
          onChange={(newStatus) => navigate(`/supplier/requests/${newStatus}`)}
        >
          <Tabs.List justify="space-between">
            <Group>
              <Tabs.Tab value="traceability">
                <RequestTabHeader
                  count={
                    statusDisplayed === InformationRequestStatus.OPENED
                      ? stats.opened
                      : statusDisplayed === InformationRequestStatus.CLOSED
                        ? stats.closed
                        : stats.canceled
                  }
                  label={t("requests.tabs.traceability")}
                  color={
                    statusDisplayed === InformationRequestStatus.OPENED
                      ? "red"
                      : "green"
                  }
                />
              </Tabs.Tab>
              {statusDisplayed !== InformationRequestStatus.CANCELED && (
                <>
                  <Tabs.Tab value="prodsitefield">
                    <RequestTabHeader
                      count={currentProdsitefieldRequests.length}
                      label={t("requests.tabs.prodsitefield")}
                      color={
                        statusDisplayed === InformationRequestStatus.OPENED
                          ? "red"
                          : "green"
                      }
                    />
                  </Tabs.Tab>
                  <Tabs.Tab value="certificates_scope">
                    <RequestTabHeader
                      count={currentScopeCertificates.length}
                      label={t("requests.tabs.certificates.scope")}
                      color={
                        statusDisplayed === InformationRequestStatus.OPENED
                          ? "red"
                          : "green"
                      }
                    />
                  </Tabs.Tab>
                  <Tabs.Tab value="certificates_transaction">
                    <RequestTabHeader
                      count={currentTransactionCertificates.length}
                      label={t("requests.tabs.certificates.transaction")}
                      color={
                        statusDisplayed === InformationRequestStatus.OPENED
                          ? "red"
                          : "green"
                      }
                    />
                  </Tabs.Tab>
                </>
              )}
            </Group>
          </Tabs.List>
          <Tabs.Panel value="prodsitefield">
            <Stack
              p="md"
              bg="var(--mantine-color-supplier-background)"
              mih="calc(100vh - 114px)"
            >
              {status === "prodsitefield" &&
                (isProductionSiteFieldRequestsLoading ? (
                  <Center h="100%">
                    <Loader />
                  </Center>
                ) : isProductionSiteFieldRequestsError ? (
                  <Text>{t("request.errors.generic")}</Text>
                ) : (
                  <ProdsiteRequests
                    prodsiteRequests={currentProdsitefieldRequests}
                  />
                ))}
            </Stack>
          </Tabs.Panel>
          <Tabs.Panel value="traceability">
            {status === "traceability" && (
              <TraceabilityRequestsList
                key={status}
                status={statusDisplayed}
                productionSites={productionSites}
                operationChains={operationChainsResponse?.data ?? []}
              />
            )}
          </Tabs.Panel>
          <Tabs.Panel value="certificates_scope">
            <Stack
              p="md"
              bg="var(--mantine-color-supplier-background)"
              mih="calc(100vh - 114px)"
            >
              {status === "certificates_scope" &&
                (isCertificateRequestsLoading ? (
                  <Center h="100%">
                    <Loader />
                  </Center>
                ) : isCertificateRequestsError ? (
                  <Text>{t("request.errors.generic")}</Text>
                ) : (
                  <CertificateRequests
                    closedRequests={closedScopeCertificates}
                    openedRequests={openScopeCertificates}
                    status={statusDisplayed}
                  />
                ))}
            </Stack>
          </Tabs.Panel>
          <Tabs.Panel value="certificates_transaction">
            <Stack
              p="md"
              bg="var(--mantine-color-supplier-background)"
              mih="calc(100vh - 114px)"
            >
              {status === "certificates_transaction" &&
                (isCertificateRequestsLoading ? (
                  <Center>
                    <Loader />
                  </Center>
                ) : isCertificateRequestsError ? (
                  <Text>{t("request.errors.generic")}</Text>
                ) : (
                  <CertificateRequestsBatchModalProvider>
                    <CertificateRequests
                      openedRequests={openTransactionCertificates}
                      closedRequests={closedTransactionCertificates}
                      status={statusDisplayed}
                    />
                  </CertificateRequestsBatchModalProvider>
                ))}
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </ActivitiesProvider>
    </>
  )
}
